import { makeApiProvider } from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { ConnectMangopayAccountBody, IOnboardingPhoenixApi } from './api';
import { OnboardingPhoenixApi } from './api';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_SERVICE_PROVIDER_PHOENIX_KEY } from '../service-provider-phoenix';
import { transformFlatErrorsToNested } from '@pflegenavi/shared/frontend';
import type {
  RecipientSchema,
  RecipientSchemaParams,
  ValidateRecipientDataRequest,
  CreateRecipientRequest,
  RecipientCreationResponse,
} from '../types/Recipient';

const ApiContext = createContext<IOnboardingPhoenixApi | undefined>(undefined);
const { useApi: useOnboardingApi, ApiProvider: OnboardingPhoenixApiProvider } =
  makeApiProvider({
    name: 'Onboarding',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new OnboardingPhoenixApi(tenantId, auth, apiUrl),
  });

export { OnboardingPhoenixApiProvider };

const ACCEPT_INVITATION_KEY = ['accept-invitation'];
const GET_NURSING_HOME_LIST_KEY = ['get-nursing-home-list'];
const CONNECT_MANGOPAY_ACCOUNT_KEY = ['get-mango-pay-account'];
const ENROLL_SCA_ENROLLMENT_KEY = ['enroll-sca-enrollment'];
const MANGOPAY_MANAGE_PROXY_CONSENT_KEY = ['mangopay-manage-proxy-consent'];
const GET_PAYOUT_METHODS_KEY = ['get-payout-methods'];
const GET_RECIPIENT_SCHEMA_KEY = ['get-recipient-schema'];
const VALIDATE_RECIPIENT_DATA_KEY = ['validate-recipient-data'];
const CREATE_RECIPIENT_KEY = ['create-recipient'];
const DEACTIVATE_RECIPIENT_KEY = ['deactivate-recipient'];
const GET_RECIPIENT_KEY = ['get-recipient'];

export const useAcceptInvitationPhoenix = (): UseMutationResult<
  void,
  unknown,
  {
    nursing_home_id: string;
  }
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    void,
    unknown,
    {
      nursing_home_id: string;
    }
  >({
    mutationKey: ACCEPT_INVITATION_KEY,
    mutationFn: (data) =>
      api.acceptInvitation({
        params: data,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GET_NURSING_HOME_LIST_KEY,
      });
    },
  });
  return result;
};

export const useConnectServiceProviderMangopayAccount = (): UseMutationResult<
  {
    status: 'OK';
    redirect_url?: string;
  },
  unknown,
  ConnectMangopayAccountBody
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    {
      status: 'OK';
      redirect_url?: string;
    },
    unknown,
    ConnectMangopayAccountBody
  >({
    mutationKey: CONNECT_MANGOPAY_ACCOUNT_KEY,
    mutationFn: (data) =>
      api.connectMangopayAccount({
        body: {
          data,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GET_NURSING_HOME_LIST_KEY,
      });
      await queryClient.invalidateQueries({
        queryKey: [GET_SERVICE_PROVIDER_PHOENIX_KEY],
      });
    },
  });
  return result;
};

export const useEnrollScaEnrollment = (): UseMutationResult<
  {
    redirect_url: string | null;
  },
  unknown,
  string
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      redirect_url: string | null;
    },
    unknown,
    string
  >({
    mutationKey: ENROLL_SCA_ENROLLMENT_KEY,
    mutationFn: (return_url: string) =>
      api.enrollScaEnrollment({
        body: {
          return_url,
        },
      }),
    onSuccess: async () => {
      // Invalidate service provider to refresh SCA enrollment status
      await queryClient.invalidateQueries({
        queryKey: [GET_SERVICE_PROVIDER_PHOENIX_KEY],
      });
    },
  });
};

export const useMangopayManageProxyConsent = (): UseMutationResult<
  {
    redirect_url: string | null;
  },
  unknown,
  string
> => {
  const api = useOnboardingApi();
  return useMutation<
    {
      redirect_url: string | null;
    },
    unknown,
    string
  >({
    mutationKey: MANGOPAY_MANAGE_PROXY_CONSENT_KEY,
    mutationFn: (return_url: string) =>
      api.mangopayManageProxyConsent({
        body: {
          return_url,
        },
      }),
  });
};

export const useGetPayoutMethods = (
  enabled = true
): UseQueryResult<string[]> => {
  const api = useOnboardingApi();
  return useQuery<string[]>({
    queryKey: [...GET_PAYOUT_METHODS_KEY, 'EUR'],
    queryFn: async () => {
      const response = await api.getPayoutMethods({
        params: { currency: 'EUR' },
      });
      return response.data.payout_methods || [];
    },
    enabled,
    refetchOnWindowFocus: false,
    staleTime: Infinity, // Static data that doesn't change during session
  });
};

export const useGetRecipientSchema = (
  params: RecipientSchemaParams,
  enabled = true
): UseQueryResult<RecipientSchema> => {
  const api = useOnboardingApi();
  return useQuery<RecipientSchema>({
    queryKey: [...GET_RECIPIENT_SCHEMA_KEY, params],
    queryFn: async () => {
      const response = await api.getRecipientSchema({ params });
      // Extract schema from the response
      return response.data.schema;
    },
    enabled,
    refetchOnWindowFocus: false,
    staleTime: Infinity, // Static data that doesn't change during session
  });
};

export const useValidateRecipientData = (): UseMutationResult<
  { valid: boolean; errors?: string[] },
  unknown,
  ValidateRecipientDataRequest
> => {
  const api = useOnboardingApi();
  return useMutation<
    { valid: boolean; errors?: string[] },
    unknown,
    ValidateRecipientDataRequest
  >({
    mutationKey: VALIDATE_RECIPIENT_DATA_KEY,
    mutationFn: async (data) => {
      const response = await api.validateRecipientData({ body: data });
      return response.data;
    },
    // Transform error responses to have nested structure
    onError: (error: unknown) => {
      if (
        error &&
        typeof error === 'object' &&
        'errors' in error &&
        Array.isArray(error.errors)
      ) {
        // Add nested errors to the error object for easier consumption
        (error as { nestedErrors?: unknown }).nestedErrors =
          transformFlatErrorsToNested(error.errors);
      }
      throw error; // Re-throw to maintain error flow
    },
  });
};

export const useCreateRecipient = (): UseMutationResult<
  RecipientCreationResponse,
  unknown,
  CreateRecipientRequest
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  return useMutation<
    RecipientCreationResponse,
    unknown,
    CreateRecipientRequest
  >({
    mutationKey: CREATE_RECIPIENT_KEY,
    mutationFn: (data) => api.createRecipient({ body: data }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GET_SERVICE_PROVIDER_PHOENIX_KEY,
      });
      await queryClient.invalidateQueries({
        queryKey: GET_RECIPIENT_KEY,
      });
    },
  });
};

export const useDeactivateRecipient = (): UseMutationResult<
  RecipientCreationResponse,
  unknown,
  void
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  return useMutation<RecipientCreationResponse, unknown, void>({
    mutationKey: DEACTIVATE_RECIPIENT_KEY,
    mutationFn: () => api.deactivateRecipient({}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GET_SERVICE_PROVIDER_PHOENIX_KEY,
      });
      await queryClient.invalidateQueries({
        queryKey: GET_RECIPIENT_KEY,
      });
    },
  });
};

export const useGetRecipient = (
  enabled = true
): UseQueryResult<
  Awaited<ReturnType<IOnboardingPhoenixApi['getRecipient']>>
> => {
  const api = useOnboardingApi();
  return useQuery({
    queryKey: GET_RECIPIENT_KEY,
    queryFn: () => api.getRecipient(),
    enabled,
  });
};
